/**
 * Keeps asset lineage presentation consistent while separating its cosmetic
 * visibility from the marketing caution compliance signal, which must remain visible.
 */
import React from 'react';
import Icon from '@amzn/meridian/icon';
import Tag from '@amzn/meridian/tag';
import type { TagType } from '@amzn/meridian/tag/tag';
import Tooltip from '@amzn/meridian/tooltip';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import { useTranslation } from 'src/arb-wrapper';
import type { AssetSourceLabel } from 'src/utils/assetSourceLabel';
import { deriveAssetSourceLabel } from 'src/utils/assetSourceLabel';
import { deriveMarketingCaution } from 'src/utils/marketingCaution';

const MARKETING_CAUTION_ICON_TEST_ID = 'asset-marketing-caution-icon';
const MARKETING_CAUTION_TOOLTIP_KEY = 'asset_marketing_caution_tooltip';
const MARKETING_CAUTION_TOOLTIP_PREFIX = 'asset-marketing-caution-tooltip';

const ASSET_SOURCE_LABEL_KEYS: Record<AssetSourceLabel, string> = {
  AUTOGEN: 'asset_source_autogen',
  BOX: 'asset_source_box',
  VAULT: 'asset_source_vault',
};

const ASSET_SOURCE_TAG_TYPES: Record<AssetSourceLabel, TagType> = {
  AUTOGEN: 'theme',
  BOX: 'success',
  VAULT: 'neutral',
};

interface MarketingCautionProps {
  readonly assetId?: string;
  readonly generationMethods?: readonly string[];
  readonly releaseDates?: ReadonlyArray<string | undefined>;
}

interface MarketingCautionTooltipProps {
  readonly assetId?: string;
  readonly children: React.ReactElement;
}

const MarketingCautionTooltip: React.FC<MarketingCautionTooltipProps> = ({
  assetId,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      id={assetId === undefined
        ? undefined
        : `${MARKETING_CAUTION_TOOLTIP_PREFIX}-${assetId}`}
      position="end"
      title={t(MARKETING_CAUTION_TOOLTIP_KEY)}
    >
      {children}
    </Tooltip>
  );
};

export const MarketingCautionIndicator: React.FC<MarketingCautionProps> = ({
  assetId,
  generationMethods,
  releaseDates,
}) => {
  if (!deriveMarketingCaution(generationMethods, releaseDates)) return null;

  return (
    <MarketingCautionTooltip assetId={assetId}>
      <Icon
        color="warning"
        data-testid={MARKETING_CAUTION_ICON_TEST_ID}
        tokens={alertTokens}
      />
    </MarketingCautionTooltip>
  );
};

export const AssetSourceTag: React.FC<MarketingCautionProps> = ({
  assetId,
  generationMethods,
  releaseDates,
}) => {
  const { t } = useTranslation();
  const sourceLabel = deriveAssetSourceLabel(generationMethods);

  if (sourceLabel === null) return null;

  const tag = (
    <Tag
      data-testid="asset-source-tag"
      type={ASSET_SOURCE_TAG_TYPES[sourceLabel]}
    >
      {t(ASSET_SOURCE_LABEL_KEYS[sourceLabel])}
    </Tag>
  );

  if (!deriveMarketingCaution(generationMethods, releaseDates)) return tag;

  return (
    <MarketingCautionTooltip assetId={assetId}>
      {tag}
    </MarketingCautionTooltip>
  );
};
